import React, {
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import {
  Avatar,
  Box,
  Divider,
  IconButton,
  InputAdornment,
  Stack,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { LeagueAdmin } from '../components/league/LeagueAdmin';
import { resizeImage } from '../api/cloudinary';
import { fetchGet, fetchPost } from '../api/common';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import { debounce } from 'lodash';

type PropTypes = {
  close: () => void;
  handlePlayerClick: (profileId: string) => void;
  members: string[];
  open: boolean;
};

export const SearchPlayerDialog = ({
  close,
  handlePlayerClick,
  members,
  open,
}: PropTypes) => {
  const theme = useTheme();
  const xsBreakpoint = useMediaQuery(theme.breakpoints.only('xs'));
  const [results, setResults] = useState<any[]>([]);
  const [textFieldValue, setTextFieldValue] = useState('');
  const textFieldRef = useRef(null);

  useEffect(() => {
    if (!textFieldValue || textFieldValue.length < 2) {
      setResults([]);
      return;
    }
    debouncedTyping(textFieldValue);

    return () => {
      debouncedTyping.cancel();
    };
  }, [textFieldValue]);

  const searchUsers = async (searchTerm: string) => {
    if (!searchTerm.length) {
      setResults([]);
      return;
    }
    const res = await fetchGet(`/users/search?term=${searchTerm}`);
    const sorted = res.sort((a, b) => a.username.localeCompare(b.username));
    console.log('sorted', sorted);
    setResults(sorted);
  };

  const debouncedTyping = debounce((term: string) => {
    searchUsers(term);
  }, 1000);

  const handleClearInput = () => {
    setTextFieldValue('');
    setResults([]);
    if (!textFieldRef.current) return;
    textFieldRef.current.focus();
  };

  return (
    <Dialog open={open} onClose={close} maxWidth="sm" fullWidth>
      <DialogContent sx={{ width: '100%', height: '100%' }}>
        <Stack spacing={1} paddingX={2}>
          <Stack direction="row" spacing={1} width={'100%'}>
            <TextField
              inputRef={textFieldRef}
              variant="outlined"
              fullWidth
              autoFocus
              size="small"
              value={textFieldValue}
              placeholder="Search players"
              autoComplete="off"
              onChange={(e) => setTextFieldValue(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="start">
                    <IconButton onClick={handleClearInput} edge="end">
                      <ClearIcon sx={{ fontSize: 18 }} />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              sx={{
                backgroundColor: 'black',
                borderRadius: 2,
                '& .MuiOutlinedInput-root': {
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderRadius: 2,
                  },
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: 'transparent',
                  },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderColor: 'transparent',
                  },
                },
              }}
            />
            <Button onClick={close}>Done</Button>
          </Stack>
          <Divider />
          <Stack spacing={2}>
            {results.map((p) => (
              <Stack direction={'row'}>
                <Avatar
                  alt={p.username}
                  src={resizeImage(p.avatarPublicId, 100, 100)}
                  sx={{
                    marginRight: 1,
                    width: 40,
                    height: 40,
                    borderRadius: 1,
                  }}
                />
                <Stack flexGrow={1}>
                  <Typography fontWeight={600}>{p.username}</Typography>
                  <Typography variant="caption" color="white">
                    {p.fullName && p.location
                      ? p.fullName + ' | ' + p.location
                      : p.fullName && !p.location
                      ? p.fullName
                      : p.location}
                  </Typography>
                </Stack>
                <Box display={'flex'} alignItems={'center'}>
                  <Button
                    variant="contained"
                    size="small"
                    onClick={() => handlePlayerClick(p._id)}
                    disabled={members.includes(p._id)}
                  >
                    {members.includes(p._id) ? 'On Team' : 'Add'}
                  </Button>
                </Box>
              </Stack>
            ))}
          </Stack>
        </Stack>
      </DialogContent>
    </Dialog>
  );
};
